/**
 * Outlet JD: mobile-only continuous swipe loop for product-card image
 * galleries (snippets/card-gallery.liquid).
 *
 * Horizon's <slideshow-component> already loops for arrow/keyboard/API
 * navigation (the `infinite` attribute is on by default - see
 * snippets/slideshow.liquid), but a native touch swipe can never scroll
 * past the last real slide because there is no DOM content beyond it, so
 * swiping stops dead at either end.
 *
 * This module is purely additive: it never touches the Slideshow class or
 * any other slideshow instance (hero, product-page gallery, search, etc).
 * It only acts on `<slideshow-component data-loop-carousel>` elements,
 * which snippets/card-gallery.liquid only marks for product-card galleries
 * with more than one image, and only once the viewport is mobile-width and
 * the user has actually started touching that specific card - so a card
 * nobody swipes never gets the extra DOM at all, and desktop never does.
 *
 * Technique: clone just the first and last real slide (one each, not the
 * whole gallery) and place them just outside the real slide range. The
 * clones reuse the exact same image URL as the real slide they mirror, so
 * the browser serves them from cache - no extra network bytes. When a
 * swipe lands on a clone, the scroll position is corrected instantly
 * (no animation, same frame) to the equivalent real slide, so the visit to
 * the clone is imperceptible and the gallery scroll position stays a
 * genuine index into the real slides.
 */

const MOBILE_QUERY = '(max-width: 749px)';
const SETTLE_DELAY = 70;

/**
 * @param {Element} scroller
 * @returns {Element | null}
 */
function centeredChild(scroller) {
  const rect = scroller.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  let closest = null;
  let closestDistance = Infinity;

  for (const child of scroller.children) {
    const childRect = child.getBoundingClientRect();
    const childCenter = childRect.left + childRect.width / 2;
    const distance = Math.abs(childCenter - centerX);

    if (distance < closestDistance) {
      closestDistance = distance;
      closest = child;
    }
  }

  return closest;
}

/**
 * @param {Element} slide
 */
function markAsClone(slide) {
  slide.setAttribute('aria-hidden', 'true');
  slide.setAttribute('inert', '');
  slide.setAttribute('tabindex', '-1');
  slide.setAttribute('data-loop-clone', '');
  slide.removeAttribute('ref');
  slide.removeAttribute('slide-id');

  for (const el of slide.querySelectorAll('[ref]')) {
    el.removeAttribute('ref');
  }
}

/**
 * @param {Element} slideshow
 */
function setupLoop(slideshow) {
  if (slideshow.hasAttribute('data-loop-ready')) return;

  const scroller = slideshow.querySelector('[ref="scroller"]');
  if (!scroller) return;

  const realSlides = Array.from(scroller.children).filter(
    (el) => el.tagName === 'SLIDESHOW-SLIDE' && !el.hasAttribute('data-loop-clone')
  );
  if (realSlides.length < 2) return;

  slideshow.setAttribute('data-loop-ready', 'true');

  const first = realSlides[0];
  const last = realSlides[realSlides.length - 1];

  const beforeFirstClone = /** @type {Element} */ (last.cloneNode(true));
  const afterLastClone = /** @type {Element} */ (first.cloneNode(true));

  markAsClone(beforeFirstClone);
  markAsClone(afterLastClone);

  scroller.insertBefore(beforeFirstClone, first);
  scroller.appendChild(afterLastClone);

  let correcting = false;
  let settleTimer;

  scroller.addEventListener(
    'scroll',
    () => {
      if (correcting) return;

      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        const current = centeredChild(scroller);

        if (current === afterLastClone) {
          correcting = true;
          scroller.scrollTo({ left: /** @type {HTMLElement} */ (first).offsetLeft, behavior: 'instant' });
          // A macrotask, not requestAnimationFrame: rAF only fires on the next paint,
          // which can be delayed well past this point when several corrections happen
          // in quick succession (each swipe re-arms one), leaving `correcting` stuck
          // `true` and silently swallowing the next real scroll settle. setTimeout(0)
          // clears the flag on the next tick regardless of paint timing.
          setTimeout(() => {
            correcting = false;
          }, 0);
        } else if (current === beforeFirstClone) {
          correcting = true;
          scroller.scrollTo({ left: /** @type {HTMLElement} */ (last).offsetLeft, behavior: 'instant' });
          setTimeout(() => {
            correcting = false;
          }, 0);
        }
      }, SETTLE_DELAY);
    },
    { passive: true }
  );
}

/**
 * Arms a slideshow so the loop is only ever built the first time the
 * shopper actually touches that specific card.
 * @param {Element} slideshow
 */
function arm(slideshow) {
  if (slideshow.hasAttribute('data-loop-armed')) return;
  slideshow.setAttribute('data-loop-armed', 'true');

  slideshow.addEventListener('pointerdown', () => setupLoop(slideshow), { once: true, passive: true });
}

function activate() {
  if (!window.matchMedia(MOBILE_QUERY).matches) return;

  for (const slideshow of document.querySelectorAll('slideshow-component[data-loop-carousel]')) {
    arm(slideshow);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', activate);
} else {
  activate();
}

/**
 * Arms product-card galleries added after the initial page render (collection
 * filtering, pagination, homepage section re-render). Scoped to a cheap
 * selector check so it stays negligible on unrelated DOM churn elsewhere on
 * the page (cart drawer, favorites, quick add, etc).
 */
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;

      if (node.matches?.('slideshow-component[data-loop-carousel]')) {
        arm(node);
      }

      node.querySelectorAll?.('slideshow-component[data-loop-carousel]').forEach(arm);
    }
  }
}).observe(document.body, { childList: true, subtree: true });
